// Shared frame of one assessment: identity block (name, institution, reference date, responsible, workshop,
// pinned questionnaire version) and the Atribuições · Diagnóstico · Prognóstico · Painel · Relatórios tabs.
import type { ReactNode } from "react";
import { BackLink } from "../../components/nav-controls.tsx";
import { GuardedLink } from "../../components/plan-shell.tsx";
import { ErrorState, Pill, Skeleton, cx } from "../../components/ui.tsx";
import { ApiError } from "../../lib/api.ts";
import { usePageTitle } from "../../lib/nav.tsx";
import { useAssessment, withInstitution, type AssessmentDetail, type MaturityInstitution } from "../../lib/maturity.ts";
import { MaturityLayout } from "./MaturityLayout.tsx";
import { NotFoundPage } from "../NotFoundPage.tsx";
import { formatDateBR } from "../../../shared/dates.ts";

export type AssessmentTab = "atribuicoes" | "diagnosis" | "prognosis" | "painel" | "relatorios";

const scenarioPill = (s: AssessmentDetail["scenarios"]["diagnosis"], none: string) =>
  !s ? <Pill tone="gray">{none}</Pill> : s.state === "finalized" ? <Pill tone="green">Encerrado</Pill> : <Pill tone="gold">Rascunho · {s.answered}/{s.total}</Pill>;

export function AssessmentShell({ institution, id, tab, title, children }: {
  institution: MaturityInstitution; id: string; tab: AssessmentTab; title: string; children: (a: AssessmentDetail) => ReactNode;
}) {
  const q = useAssessment(institution.id, id);
  usePageTitle(title, q.data?.name);
  if (q.isPending) return <MaturityLayout wide><Skeleton lines={6} /></MaturityLayout>;
  if (q.isError) {
    if (q.error instanceof ApiError && q.error.status === 404) return <NotFoundPage />;
    return <MaturityLayout wide><ErrorState error={q.error} onRetry={() => void q.refetch()} /></MaturityLayout>;
  }
  const a = q.data;
  const tabs: { key: AssessmentTab; label: string; href: string; pill?: ReactNode }[] = [
    { key: "atribuicoes", label: "Atribuições", href: `/diagnosticos/${a.id}/atribuicoes` },
    { key: "diagnosis", label: "Diagnóstico", href: `/diagnosticos/${a.id}/avaliacao/diagnosis`, pill: scenarioPill(a.scenarios.diagnosis, "—") },
    { key: "prognosis", label: "Prognóstico", href: `/diagnosticos/${a.id}/avaliacao/prognosis`, pill: scenarioPill(a.scenarios.prognosis, "Não iniciado") },
    { key: "painel", label: "Painel", href: `/diagnosticos/${a.id}/painel` },
    { key: "relatorios", label: "Relatórios", href: `/diagnosticos/${a.id}/relatorios` },
  ];
  return (
    <MaturityLayout wide>
      <div className="mb-2"><BackLink fallback={withInstitution("/diagnosticos", institution.id)} label="Avaliações" /></div>
      <header className="mb-5 animate-fade-in">
        <p className="eyebrow">{a.institution.name} · Diagnóstico institucional</p>
        <h1 id="page-title" tabIndex={-1} className="mt-1 text-[26px] leading-tight font-bold tracking-tight outline-none">{a.name}</h1>
        <p className="mt-1 text-sm text-muted">
          Referência <span className="num">{formatDateBR(a.reference_date)}</span> · Responsável: {a.responsible_label}
          {a.linked_plan ? ` · Oficina: ${a.linked_plan.name}` : ""}
        </p>
        <p className="mt-0.5 text-xs text-ink-3">
          {a.template.name}, versão {a.template.revision_number} · método {a.template.method_code}
          {a.based_on ? ` · Baseada em “${a.based_on.name}”` : ""}
        </p>
      </header>
      <nav aria-label="Etapas da avaliação" className="mb-6 flex gap-1 overflow-x-auto border-b border-line">
        {tabs.map((t) => (
          <GuardedLink key={t.key} href={withInstitution(t.href, institution.id)} aria-current={t.key === tab ? "page" : undefined}
            className={cx("-mb-px inline-flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm whitespace-nowrap transition-colors duration-[var(--duration-base)]",
              t.key === tab ? "border-accent font-semibold text-primary" : "border-transparent text-muted hover:text-ink")}>
            {t.label}{t.pill}
          </GuardedLink>
        ))}
      </nav>
      {children(a)}
    </MaturityLayout>
  );
}
